import { AlarmLogResponse, getAlarmLogList, readAlarmLog } from '@/api/alarm';
import {
  AlarmLevel,
  AlarmLevelColorDisplay,
  AlarmLevelDisplay,
  AlarmState,
  AlarmStateColorDisplay,
  AlarmStateDisplay,
} from '@/shared/types';
import {
  Dropdown,
  EllipsisSpan,
  getBase,
  Modal,
  Paging,
  ShowInput,
  Table,
  Tag,
  usePaging,
  useUpdate,
} from '@maxtropy/components';
import { Space } from 'antd';
import dayjs from 'dayjs';
import React, { FC, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { AlarmRecordsAnalysisContext } from '../..';
import qs from 'qs';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { qingflowWorkDetailPath } from '@/pages/AlarmRecord/const';
import AssociateLoopsModal from '@/pages/AlarmRecord/List/AssociateLoopsModal';
import { isNil } from 'lodash-es';

export interface IAlarmRecordModal {
  alarmModalOpen: boolean;
  setListNumber?: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const displayDateFormat = 'YYYY/MM/DD HH:mm:ss';

const columns = [
  {
    title: '设备名称',
    dataIndex: 'deviceName',
    ellipsis: { showTitle: true },
    width: 180,
    fixed: true,
    render: (v: string, record: AlarmLogResponse) => {
      return (
        <Link to={`/device/manage/device/${record?.deviceId}/detail`} target="_blank">
          <EllipsisSpan value={v} />
        </Link>
      );
    },
  },
  {
    title: '报警等级',
    dataIndex: 'alarmLevel',
    width: 100,
    ellipsis: { showTitle: true },
    fixed: true,
    render: (v: AlarmLevel) => (
      <ShowInput
        value={
          <Tag border="solid" color={AlarmLevelColorDisplay[v]}>
            {AlarmLevelDisplay[v]}
          </Tag>
        }
      />
    ),
  },
  {
    title: '报警状态',
    dataIndex: 'state',
    width: 100,
    ellipsis: { showTitle: true },
    fixed: true,
    render: (v: AlarmState) => (
      <ShowInput
        value={
          <Tag border="solid" color={AlarmStateColorDisplay[v]}>
            {AlarmStateDisplay[v]}
          </Tag>
        }
      />
    ),
  },

  {
    title: '报警信息',
    width: 180,
    dataIndex: 'alarmName',
    ellipsis: { showTitle: true },
    render: (v: string, record: any) => {
      return <EllipsisSpan value={v} />;
    },
  },
  {
    title: '报警类型',
    width: 180,
    dataIndex: 'ruleLabelNames',
    ellipsis: { showTitle: true },
    render: (v: string[], record: any) => {
      return <EllipsisSpan value={v ? v.join(',') : '--'} />;
    },
  },
  {
    title: '区域',
    width: 180,
    dataIndex: 'areaLabelNames',
    ellipsis: { showTitle: true },
    render: (v: string[], record: any) => {
      return <EllipsisSpan value={v ? v.join(',') : '--'} />;
    },
  },

  {
    title: '报警时间',
    width: 180,
    dataIndex: 'alarmTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : undefined} />,
  },
  {
    title: '恢复时间',
    width: 180,
    dataIndex: 'recoveryTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : '未恢复'} />,
  },
  {
    title: '报警持续时长',
    width: 180,
    dataIndex: 'duration',
    ellipsis: { showTitle: true },
    render: (v: string, record: any) => {
      return <EllipsisSpan value={v} />;
    },
  },
];

const AlarmRecordModal: FC<IAlarmRecordModal> = props => {
  const { alarmModalOpen, setListNumber } = props;
  const { selectedSceneId, activeKey, searchParams, sceneTabList } = useContext(AlarmRecordsAnalysisContext);
  const [updateState, updateFn] = useUpdate();
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const hasCreateWorkOrderPers = useHasPermission(PermissionsType.B_WORKER_ORDER_CREATE);
  const hasSubmitOrderPers = useHasPermission(PermissionsType.B_SUBMIT_WORKER_ORDER);
  const hasViewEntityDetails = useHasPermission(PermissionsType.B_VIEWENTITYDETAILS);
  const hasCopyRuleNumber = useHasPermission(PermissionsType.B_COPYRULENUMBER);
  const hasConfirmReadButton = useHasPermission(PermissionsType.B_CONFIRMREADBUTTON);

  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [displayAssociate, setDisplayAssociate] = useState<string>();
  const [isSortValue, setIsSortValue] = useState<boolean>(false); // 是否传sortvalue
  const [sortValues, setSortValues] = useState<any[]>();

  // 报警记录
  const { data: alarmRecordList, loading } = useRequest(
    async () => {
      const res = await getAlarmLogList({
        ...searchParams,
        deviceLabelIds: selectedSceneId ? [selectedSceneId] : (sceneTabList ?? []).map(i => i.id!),
        sortValues: isSortValue ? sortValues : undefined,
        page: pageOffset,
        size: pageSize,
        state: activeKey === 'all' ? undefined : Number(activeKey),
      });
      if (res) {
        if (res.useSearchAfter) {
          setSortValues(res.sortValues);
          setIsSortValue(true);
        } else {
          setSortValues(undefined);
          setIsSortValue(false);
        }
        setListNumber?.(res.total);
        setTotalCount(res.total);
      }
      return res.list ?? [];
    },
    {
      ready: !!pageSize && !!pageOffset && alarmModalOpen,
      refreshDeps: [pageSize, pageOffset, searchParams, updateState, alarmModalOpen],
    }
  );

  const readLog = (id: number) => {
    readAlarmLog(id).then(res => {
      if (res.flag) {
        Modal.success({
          title: '已读！',
          okText: '确定',
          onOk() {
            updateFn();
          },
          onCancel() {
            updateFn();
          },
        });
      }
    });
  };

  const onCopy = (v: string | undefined) => {
    if (!v) return;
    if (getBase()) {
      window.rawWindow.navigator.clipboard.writeText(v);
    } else {
      navigator.clipboard.writeText(v);
    }
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 80,
      showForce: true,
      fixed: 'right' as const,
      render: (value: undefined, record: AlarmLogResponse) => {
        return (
          <Space size={16}>
            <Dropdown
              type="link"
              menu={{
                items: [
                  {
                    show: hasConfirmReadButton && !window.alarmRecordReadTimeHide,
                    key: '1',
                    disabled: !!record?.readTime,
                    label: (
                      <span
                        onClick={() => {
                          readLog(record.id);
                        }}
                      >
                        {record?.readTime ? '已读' : '确认已读'}
                      </span>
                    ),
                  },
                  {
                    show: hasSubmitOrderPers,
                    key: '2',
                    label: (
                      <span
                        onClick={() => {
                          window.open(
                            `${window.ALARMWORKORDER}/orderv2/order/add/index?${qs.stringify(
                              {
                                problemSource: 10,
                                operationUnitCode: (record.ous?.map(item => item.id) ?? []).join(','),
                                alarmId: record.id,
                                alarmTypeCode: -1,
                              },
                              { indices: false }
                            )}`,
                            '_self'
                          );
                        }}
                      >
                        发起工单
                      </span>
                    ),
                  },
                  {
                    show: hasCreateWorkOrderPers && record.ruleLogWorkOrderId,
                    key: '3',
                    label: <span onClick={() => window.open(qingflowWorkDetailPath, '_blank')}>查看工单</span>,
                  },
                  {
                    show: hasCreateWorkOrderPers && !record.ruleLogWorkOrderId,
                    key: '4',
                    label: <Link to={`/device/alarm/record/${record.id}/create`}>创建工单</Link>,
                  },
                  {
                    show: true,
                    key: '5',
                    label: (
                      /* 点击设备数据查询关联属性 */
                      <Link
                        to={`/data/history/device?deviceId=${record.deviceId}&alarmTime=${dayjs(
                          record.alarmTime
                        ).valueOf()}`}
                        target="_blank"
                      >
                        查看设备数据
                      </Link>
                    ),
                  },
                  {
                    show: true,
                    key: '6',
                    label: (
                      <span
                        onClick={() =>
                          window.open(`/device/alarm/record/detail/${record.id}?state=${record?.state}`, '_blank')
                        }
                      >
                        查看报警详情
                      </span>
                    ),
                  },
                  {
                    show: hasCopyRuleNumber,
                    key: '7',
                    label: <span onClick={() => onCopy(record.alarmCode)}>复制规则编号</span>,
                  },
                  {
                    show: hasViewEntityDetails,
                    key: '8',
                    label: (
                      <span
                        onClick={() => {
                          setIsVisibleModal(true);
                          setDisplayAssociate(record.deviceId);
                        }}
                      >
                        查看关联实体
                      </span>
                    ),
                  },
                ].filter(item => item.show),
              }}
            >
              操作项
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        rowKey="id"
        sticky
        loading={loading}
        scroll={{ y: 500 }}
        dataSource={alarmRecordList ?? []}
        columns={buildColumns}
        pagination={false}
      />
      <Paging pagingInfo={pagingInfo} />
      {isVisibleModal && <AssociateLoopsModal visible={setIsVisibleModal} displayAssociate={displayAssociate!} />}
    </>
  );
};

export default AlarmRecordModal;
